import { useEffect, type RefObject } from 'react';
import { useSeqLog } from '@/hooks/telemetry/use-seq-log';
import {
  CMESSAGEMODAL_SEQ_COMPONENT,
  SCROLL_DIMENSIONS_MARKER_NAME,
} from './messageModalConstants';
import { buildMessageModalScrollDimensionsSnapshot } from './messageModalScrollSnapshot';

interface UseMessageModalScrollDimensionsInput {
  visible: boolean;
  isMobile: boolean;
  vw: number;
  vh: number;
  rootRef: RefObject<HTMLDivElement | null>;
}

export function useMessageModalScrollDimensions({
  visible,
  isMobile,
  vw,
  vh,
  rootRef,
}: UseMessageModalScrollDimensionsInput): void {
  const log = useSeqLog(CMESSAGEMODAL_SEQ_COMPONENT);

  useEffect((): (() => void) | undefined => {
    if (!visible || !isMobile) {
      return undefined;
    }

    const measure = (): void => {
      const body = document.body;

      log(SCROLL_DIMENSIONS_MARKER_NAME, buildMessageModalScrollDimensionsSnapshot({
        root: rootRef.current,
        body,
        html: document.documentElement,
        visualViewport: window.visualViewport,
        bodyZoom: getComputedStyle(body).zoom,
        windowInnerWidth: window.innerWidth,
        windowInnerHeight: window.innerHeight,
        windowScrollX: window.scrollX,
        windowScrollY: window.scrollY,
        vw,
        vh,
      }));
    };

    const raf = requestAnimationFrame(measure);
    window.addEventListener('scroll', measure, { passive: true });

    return (): void => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', measure);
    };
  }, [isMobile, log, rootRef, vh, visible, vw]);
}
